import { useMemo, useState } from "react";
import { Card, Col, Row, Statistic, Table, Tag, Tooltip as AntTooltip, Typography } from "antd";
import {
  LineChartOutlined,
  ShopOutlined,
  ShoppingOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { apiRequest } from "../api/client";
import {
  type AuditLog,
  type ListResponse,
  auditLogsApi,
  channelScrapersApi,
  channelsApi,
  priceSnapshotsApi,
  standardProductsApi,
} from "../api/resources";
import type { Channel, ScraperRun } from "@glow-price/shared";

// ─── deterministic 30-day price trend (no real API yet) ────────────────────
// Math.sin keeps the curve stable across renders so designers can preview the
// component without flicker. Replace with a real time-series API when ready.
const TREND = Array.from({ length: 30 }, (_, i) => ({
  day: "D" + i,
  price: Math.round(380 + Math.sin(i / 3) * 40 + i * 2),
}));

const CHANNEL_TYPE_LABEL: Record<string, string> = {
  duty_free: "免税店",
  official: "品牌官方",
  brand_authorized: "品牌授权",
  self_operated: "自营平台",
  third_party: "第三方平台",
};

const PIE_COLORS = ["#6d2839", "#a87830", "#2e5d3a", "#2e5d8a", "#a83d4a", "#8a8580"];

const CARD_BODY_PADDING = 24;

export function DashboardPage() {
  // ─── KPI: total standard products ──────────────────────────────────────
  const productsQuery = useQuery({
    queryKey: ["dashboard", "standard-products-total"],
    queryFn: () => standardProductsApi.list({ pageSize: 1 }),
  });

  // ─── KPI: total channels ───────────────────────────────────────────────
  const channelsQuery = useQuery({
    queryKey: ["dashboard", "channels-total"],
    queryFn: () => channelsApi.list({ pageSize: 1 }),
  });

  // ─── KPI: snapshots in the last 24 hours ───────────────────────────────
  // priceSnapshotsApi already supports `sinceCapturedAt`, so we ask for
  // pageSize=1 and read `total` from the envelope.
  const since24h = useMemo(
    () => new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    [],
  );
  const snapshots24hQuery = useQuery({
    queryKey: ["dashboard", "snapshots-24h", since24h],
    queryFn: () =>
      priceSnapshotsApi.list({ sinceCapturedAt: since24h, pageSize: 1 }),
  });

  // ─── KPI delta: snapshots between 24h-48h window (for WoW comparison) ──
  // Used to compute a real day-over-day delta for the 24h snapshot KPI.
  // No global week-over-week endpoint exists today — see TODO(api) below.
  const since48h = useMemo(
    () => new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
    [],
  );
  const snapshotsPrev24hQuery = useQuery({
    queryKey: ["dashboard", "snapshots-prev24h", since48h],
    queryFn: () =>
      priceSnapshotsApi.list({ sinceCapturedAt: since48h, pageSize: 1 }),
  });

  // Day-over-day delta percent for the 24h snapshot KPI. Returns null when we
  // cannot compute it (no prior data) so the UI can hide the line gracefully.
  const snapshots24hDeltaPct = useMemo<number | null>(() => {
    const now = snapshots24hQuery.data?.total;
    const totalPrev48h = snapshotsPrev24hQuery.data?.total;
    if (now == null || totalPrev48h == null) return null;
    const prev = totalPrev48h - now; // snapshots in [now-48h, now-24h]
    if (prev <= 0) return null;
    return Math.round(((now - prev) / prev) * 100);
  }, [snapshots24hQuery.data, snapshotsPrev24hQuery.data]);

  // ─── KPI: active scrapers ──────────────────────────────────────────────
  // channelScrapersApi.list() returns ALL channels paired with their scraper
  // config (or null). "Active" = scraper present AND enabled.
  const scrapersQuery = useQuery({
    queryKey: ["dashboard", "channel-scrapers"],
    queryFn: () => channelScrapersApi.list(),
  });
  const activeScraperCount = useMemo(
    () =>
      (scrapersQuery.data?.items ?? []).filter((r) => r.scraper?.enabled === true)
        .length,
    [scrapersQuery.data],
  );

  // ─── channel distribution (group all channels by type) ─────────────────
  // We need the full list to bucket by type, not just total. pageSize=200 is
  // a pragmatic cap — there are ~20 channels today.
  const allChannelsQuery = useQuery({
    queryKey: ["dashboard", "channels-all"],
    queryFn: () => channelsApi.list({ pageSize: 200 }),
  });

  const channelDistribution = useMemo(() => {
    const items = allChannelsQuery.data?.items ?? [];
    const byType = new Map<string, number>();
    for (const c of items) {
      byType.set(c.type, (byType.get(c.type) ?? 0) + 1);
    }
    return Array.from(byType.entries()).map(([type, value]) => ({
      type,
      name: CHANNEL_TYPE_LABEL[type] ?? type,
      value,
    }));
  }, [allChannelsQuery.data]);

  // ─── last 10 audit logs ────────────────────────────────────────────────
  const auditLogsQuery = useQuery({
    queryKey: ["dashboard", "audit-logs"],
    queryFn: () => auditLogsApi.list({ pageSize: 10 }),
  });

  // ─── last 10 scraper runs (global, with per-channel fallback) ──────────
  // TODO(api): backend currently exposes /api/admin/channel-scrapers/:id/runs
  // per-channel, but no global "all recent runs" endpoint. We probe the
  // intended URL first; if it 404s we degrade to fetching runs from the first
  // enabled scraper as a placeholder. We track which mode succeeded so the
  // panel can label itself honestly (e.g. "per-channel fallback" badge) until
  // the global endpoint lands.
  const [runsMode, setRunsMode] = useState<"global" | "fallback" | "empty">("global");
  const [fallbackChannelId, setFallbackChannelId] = useState<string | null>(null);
  const recentRunsQuery = useQuery({
    queryKey: ["dashboard", "scraper-runs", scrapersQuery.data?.items?.length ?? 0],
    enabled: !!scrapersQuery.data,
    queryFn: async (): Promise<ListResponse<ScraperRun>> => {
      try {
        const res = await apiRequest<ListResponse<ScraperRun>>(
          "/api/admin/channel-scrapers/runs",
          { query: { pageSize: 10 } },
        );
        setRunsMode("global");
        setFallbackChannelId(null);
        return res;
      } catch {
        const firstEnabled = (scrapersQuery.data?.items ?? []).find(
          (r) => r.scraper?.enabled,
        );
        if (!firstEnabled) {
          setRunsMode("empty");
          setFallbackChannelId(null);
          return { items: [], total: 0, page: 1, pageSize: 10 };
        }
        setRunsMode("fallback");
        setFallbackChannelId(firstEnabled.channel.id);
        return channelScrapersApi.listRuns(firstEnabled.channel.id, {
          pageSize: 10,
        });
      }
    },
  });

  const channelLookup = useMemo(() => {
    const m = new Map<string, Channel>();
    for (const c of allChannelsQuery.data?.items ?? []) m.set(c.id, c);
    // scraper rows also carry the channel — useful when the runs endpoint
    // returns scraper ids that the channels list didn't cover.
    for (const r of scrapersQuery.data?.items ?? []) m.set(r.channel.id, r.channel);
    return m;
  }, [allChannelsQuery.data, scrapersQuery.data]);

  const fallbackChannelName = useMemo(() => {
    if (runsMode !== "fallback" || !fallbackChannelId) return null;
    return channelLookup.get(fallbackChannelId)?.name ?? fallbackChannelId.slice(0, 8);
  }, [runsMode, fallbackChannelId, channelLookup]);

  // ─── render ────────────────────────────────────────────────────────────
  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        概览
      </Typography.Title>

      {/* Top row: 4 KPI cards */}
      {/* NOTE: only the "24h 快照" KPI has a real delta today; backend exposes
          no historical totals for products/channels/active-scrapers, so those
          cards intentionally render no delta line. TODO(api): add /stats/wow. */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card styles={{ body: { padding: CARD_BODY_PADDING } }}>
            <Statistic
              title="标准商品"
              value={productsQuery.data?.total ?? 0}
              loading={productsQuery.isLoading}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: "#6d2839" }}
            />
            <div style={{ fontSize: 11, color: "#8a8580", marginTop: 4 }}>
              全量
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card styles={{ body: { padding: CARD_BODY_PADDING } }}>
            <Statistic
              title="渠道数"
              value={channelsQuery.data?.total ?? 0}
              loading={channelsQuery.isLoading}
              prefix={<ShopOutlined />}
              valueStyle={{ color: "#2e5d8a" }}
            />
            <div style={{ fontSize: 11, color: "#8a8580", marginTop: 4 }}>
              全量
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card styles={{ body: { padding: CARD_BODY_PADDING } }}>
            <Statistic
              title="24h 快照"
              value={snapshots24hQuery.data?.total ?? 0}
              loading={snapshots24hQuery.isLoading}
              prefix={<LineChartOutlined />}
              valueStyle={{ color: "#a87830" }}
            />
            <div style={{ fontSize: 11, color: "#8a8580", marginTop: 4 }}>
              {snapshots24hDeltaPct == null
                ? "vs 前一日 —"
                : `vs 前一日 ${snapshots24hDeltaPct >= 0 ? "+" : ""}${snapshots24hDeltaPct}%`}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card styles={{ body: { padding: CARD_BODY_PADDING } }}>
            <Statistic
              title="活跃采集器"
              value={activeScraperCount}
              loading={scrapersQuery.isLoading}
              prefix={<SyncOutlined />}
              valueStyle={{ color: "#2e5d3a" }}
            />
            <div style={{ fontSize: 11, color: "#8a8580", marginTop: 4 }}>
              共 {scrapersQuery.data?.items?.length ?? 0} 配置
            </div>
          </Card>
        </Col>
      </Row>

      {/* Middle row: trend chart + channel distribution */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card
            title="30 天价格趋势"
            styles={{ body: { padding: CARD_BODY_PADDING } }}
          >
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={TREND}
                  margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="2 4" stroke="#e8e2dc" vertical={false} />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 11, fill: "#8a8580" }}
                    axisLine={{ stroke: "#e8e2dc" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#8a8580" }}
                    axisLine={false}
                    tickLine={false}
                    width={36}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid #e8e2dc",
                      boxShadow: "0 4px 12px rgba(20,10,5,0.08)",
                    }}
                    labelStyle={{ color: "#8a8580" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke="#6d2839"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title="渠道类型分布"
            styles={{ body: { padding: CARD_BODY_PADDING } }}
            loading={allChannelsQuery.isLoading}
          >
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={channelDistribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label
                  >
                    {channelDistribution.map((entry, idx) => (
                      <Cell
                        key={entry.type}
                        fill={PIE_COLORS[idx % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Bottom row: audit logs + scraper runs */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card
            title="最近 10 条审计日志"
            styles={{ body: { padding: CARD_BODY_PADDING } }}
          >
            <Table<AuditLog>
              size="small"
              rowKey="id"
              pagination={false}
              loading={auditLogsQuery.isLoading}
              dataSource={auditLogsQuery.data?.items ?? []}
              columns={[
                {
                  title: "时间",
                  dataIndex: "createdAt",
                  width: 140,
                  render: (v: string) => (
                    <span style={{ fontSize: 12, color: "#8a8580" }}>
                      {new Date(v).toLocaleString()}
                    </span>
                  ),
                },
                { title: "操作人", dataIndex: "adminUsername", width: 110 },
                { title: "动作", dataIndex: "action", width: 120 },
                {
                  title: "对象",
                  dataIndex: "entityType",
                  render: (v: string, row) => (
                    <span>
                      <Tag>{v}</Tag>
                      {row.entityId ? (
                        <span style={{ color: "#8a8580", fontSize: 11 }}>
                          {row.entityId.slice(0, 8)}
                        </span>
                      ) : null}
                    </span>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={
              runsMode === "fallback" && fallbackChannelName
                ? `最近 10 次采集运行（仅显示「${fallbackChannelName}」渠道）`
                : "最近 10 次采集运行"
            }
            extra={
              runsMode === "fallback" ? (
                <AntTooltip title="后端尚未提供全局运行列表接口，临时回退展示首个启用渠道的运行历史">
                  <Tag color="warning">单渠道回退</Tag>
                </AntTooltip>
              ) : undefined
            }
            styles={{ body: { padding: CARD_BODY_PADDING } }}
          >
            <Table<ScraperRun>
              size="small"
              rowKey="id"
              pagination={false}
              loading={recentRunsQuery.isLoading}
              dataSource={recentRunsQuery.data?.items ?? []}
              columns={[
                {
                  title: "渠道",
                  dataIndex: "channelScraperId",
                  width: 140,
                  render: (id: string) =>
                    channelLookup.get(id)?.name ?? (
                      <span style={{ color: "#8a8580", fontSize: 11 }}>
                        {id.slice(0, 8)}
                      </span>
                    ),
                },
                {
                  title: "状态",
                  dataIndex: "status",
                  width: 80,
                  render: (s: ScraperRun["status"]) => (
                    <Tag
                      color={
                        s === "success" ? "success" : s === "failed" ? "error" : "processing"
                      }
                    >
                      {s}
                    </Tag>
                  ),
                },
                {
                  title: "快照",
                  dataIndex: "insertedCount",
                  width: 70,
                  align: "right",
                },
                {
                  title: "错误",
                  dataIndex: "errorMessage",
                  ellipsis: true,
                  render: (v: string | null) =>
                    v ? (
                      <span style={{ color: "#a83d4a", fontSize: 12 }}>{v}</span>
                    ) : (
                      <span style={{ color: "#8a8580", fontSize: 12 }}>—</span>
                    ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
